import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  Stack,
  Alert,
  Box,
  Divider,
  Card,
  CardContent,
  Snackbar,
} from "@mui/material";
import { 
  CheckCircle, 
  Schedule,
  Error,
  Receipt as ReceiptIcon,
  Download,
  WhatsApp,
  ArrowBack
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toBlob } from "html-to-image";
import CompanyLogo from "../../components/CompanyLogo";

/**
 * Receipt Component with Solid Borders Instead of Shadows
 * - PDF download functionality
 * - WhatsApp image sharing
 * - Back button navigation
 * - Centered company logo at bottom
 * - Solid border styling
 */
export default function Receipt() {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();
  
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Status configuration
  const statusConfig = {
    SUCCESS: {
      color: "#4caf50",
      bgColor: "#e8f5e8",
      icon: <CheckCircle sx={{ color: "#4caf50" }} />,
      text: "Success"
    },
    PENDING: {
      color: "#ff9800",
      bgColor: "#fff3e0",
      icon: <Schedule sx={{ color: "#ff9800" }} />,
      text: "Pending"
    },
    FAILED: {
      color: "#f44336",
      bgColor: "#ffebee",
      icon: <Error sx={{ color: "#f44336" }} />,
      text: "Failed"
    }
  };

  // Decode receipt data from URL
  useEffect(() => {
    const param = new URLSearchParams(location.search).get("od");
    if (!param) {
      setError("No receipt data provided");
      return;
    }
    try {
      const obj = JSON.parse(atob(param));
      setData({
        ...obj,
        payment_amount: Number(obj.payment_amount || 0),
        convenience_fee: Number(obj.convenience_fee || 0),
        plan_price: obj.plan_price ? Number(obj.plan_price) : null,
      });
    } catch (e) {
      console.error(e);
      setError("Invalid receipt data");
    }
  }, [location.search]);

  // Show snackbar message
  const showSnackbar = (message) => {
    setSnackbar({ open: true, message });
  };

  // Generate and download PDF
  const handlePdfDownload = async () => {
    if (!printRef.current) return;
    setBusy(true);
    
    try {
      const canvas = await html2canvas(printRef.current, { 
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        allowTaint: true,
      });
      
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ 
        unit: "mm", 
        format: "a4",
        orientation: "portrait" 
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Calculate scaling to fit content properly
      const ratio = Math.min(
        pdfWidth / (imgWidth * 0.264583),
        pdfHeight / (imgHeight * 0.264583)
      );
      const scaledWidth = imgWidth * 0.264583 * ratio;
      const scaledHeight = imgHeight * 0.264583 * ratio;
      
      // Center the content on the page
      const x = (pdfWidth - scaledWidth) / 2;
      const y = Math.max(10, (pdfHeight - scaledHeight) / 2);
      
      pdf.addImage(img, "PNG", x, y, scaledWidth, scaledHeight);
      
      // Download the PDF
      pdf.save(`Receipt_${data.order_id}_${Date.now()}.pdf`);
      showSnackbar("PDF downloaded successfully!");
      
    } catch (e) {
      console.error(e);
      showSnackbar("Failed to generate PDF");
    } finally {
      setBusy(false);
    }
  };

  // Share image to WhatsApp
  const shareToWhatsApp = async () => {
    if (!printRef.current) return;
    setBusy(true);
    
    try {
      const blob = await toBlob(printRef.current, { 
        backgroundColor: "#fff",
        quality: 0.95,
        pixelRatio: 2
      });
      
      if (!blob) throw new Error("Failed to generate image");
      
      const file = new File([blob], `Receipt_${data.order_id}.png`, {
        type: "image/png",
      });
      
      const shareText = `Payment Receipt - ${data.payment_status}
💰 Amount: ₹${data.payment_amount.toLocaleString("en-IN")}
📋 Order ID: ${data.order_id}
🏦 Account: ${data.loan_number}
✅ Status: ${data.payment_status}`;

      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Payment Receipt",
          text: shareText,
          files: [file]
        });
        showSnackbar("Shared to WhatsApp successfully!");
      } else {
        // Fallback to WhatsApp Web
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
        window.open(whatsappUrl, '_blank');
        showSnackbar("Opening WhatsApp...");
      }
    } catch (e) {
      console.error(e);
      showSnackbar("Failed to share to WhatsApp");
    } finally {
      setBusy(false);
    }
  };

  // Back to history
  const goBack = () => navigate("/history", { replace: true });

  // Format currency
  const formatCurrency = (amount) => `₹${amount.toLocaleString("en-IN")}`;

  // Format date
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Error state
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button onClick={goBack} variant="contained">
          Back to History
        </Button>
      </Container>
    );
  }

  // Loading state
  if (!data) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, textAlign: "center" }}>
        <Typography variant="h6">Loading receipt...</Typography>
      </Container>
    );
  }

  const status = statusConfig[data.payment_status] || statusConfig.PENDING;

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #ffffffff 0%, #f4f4f4ff 100%)",
        minHeight: "100vh",
        py: 4,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Container maxWidth="sm"  sx={{ py: 2,
          mb:2}} >
        {/* Main Receipt Card */}
        <Paper
          ref={printRef}
          elevation={0}
          sx={{
            p: 4,mb:2,
            borderRadius: 3,
            background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: 0,
            border: "2px solid #e0e0e0",
            margin: "0 auto",
            maxWidth: "600px",
          }}
        >
          {/* Header Section - Centered */}
          <Box textAlign="center" mb={1}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: "#2c3e50" }}
            >
              Payment Receipt
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Transaction Confirmation
            </Typography>
          </Box>

          {/* Status Card - Centered */}
          

          {/* Amount Display - Centered */}
          <Box display="flex" justifyContent="center" >
            <Card
              sx={{
                background: "white",
                borderRadius: 2,
                minWidth: "300px",
                boxShadow: 0,
                 p: 1
                // border: "2px solid #e0e0e0",
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: status.color }}>
                  {formatCurrency(data.payment_amount)}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Amount Paid
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box display="flex" justifyContent="center" >
            <Card
              sx={{
                borderRadius: 2,
                minWidth: "200px",
                boxShadow: 0,
                // border: `2px solid ${status.color}`,
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                  {status.icon}
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: status.color }}
                  >
                    {status.text}
                  </Typography>
               
                </Stack>
              </CardContent>
                 <Divider sx={{ mb: 1,color: "2px solid #e0e0e0" }} />
            </Card>
          </Box>

          {/* Transaction Details - Properly Aligned */}
          <Card sx={{ 
            mb: 1, 
            borderRadius: 2,
            boxShadow: 0,
            border: "2px solid #e0e0e0",
          }}>
            <CardContent>
              <Box textAlign="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  <ReceiptIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                  Transaction Details
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={3} sx={{ textAlign: "left" }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Order ID
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ wordBreak: "break-word" }}>
                    {data.order_id}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Account Number
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {data.loan_number}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Receipt Number
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {data.reciept_no}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Transaction Date
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatDate(data.payment_completion_time)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payment Breakdown - Centered Content */}
          {/* <Card sx={{ 
            mb: 4, 
            borderRadius: 2,
            boxShadow: 0,
            border: "2px solid #e0e0e0",
          }}>
            <CardContent>
              <Box textAlign="center" mb={2}>
                <Typography variant="h6" fontWeight="bold">
                  Payment Breakdown
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">Amount Paid</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    {formatCurrency(data.payment_amount)}
                  </Typography>
                </Box>
                
                {data.convenience_fee > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">Convenience Fee</Typography>
                    <Typography variant="body1" fontWeight="500">
                      {formatCurrency(data.convenience_fee)}
                    </Typography>
                  </Box>
                )}
                
                {data.plan_price != null && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1">Plan Price</Typography>
                    <Typography variant="body1" fontWeight="500">
                      {formatCurrency(data.plan_price)}
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Transaction Date
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {formatDate(data.payment_completion_time)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card> */}

          {/* Company Logo at Bottom Center */}
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center"
            textAlign="center" 
            mt={4}
          >
            <CompanyLogo width="140px" />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Thank you for your payment!
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This is a computer-generated receipt
            </Typography>
          </Box>
          <Divider sx={{ mb: 1,color: "2px solid #e0e0e0" }} />
        </Paper>

        {/* Action Buttons - Centered and Properly Aligned */}
        <Card
          sx={{
            mt: 3,
            p: 3,
            borderRadius: 3,
            background: "rgba(255,255,255,0.9)",
            backdropFilter: "blur(10px)",
            maxWidth: "600px",
            margin: "18px auto 0",
            boxShadow: 0,
            border: "2px solid #e0e0e0",
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Typography variant="h6" textAlign="center" fontWeight="400">
              Download & Share Options
            </Typography>
            
            {/* Three Action Buttons - Perfectly Centered */}
            <Stack 
              direction={{ xs: "column", sm: "row" }} 
              spacing={{ xs: 2, sm: 1.5 }}
              justifyContent="center"
              alignItems="center"
              sx={{ 
                width: "100%",
                maxWidth: "100%",
                px: 1,
                margin: "14px 0",
                overflow: "hidden"
              }}
            >
              <Button
                variant="outlined"
                size="medium"
                startIcon={<Download />}
                onClick={handlePdfDownload}
                disabled={busy}
                sx={{
                  minWidth: { xs: "100%", sm: 150 },
                  maxWidth: { xs: "100%", sm: 150 },
                  height: 48,
                  color: "#f44336",
                  borderColor: "#f44336",
                  borderWidth: "2px",
                  fontSize: { xs: "0.875rem", sm: "0.7rem", md: "0.875rem" },
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#d32f2f",
                    borderWidth: "2px",
                    backgroundColor: "rgba(244, 67, 54, 0.04)",
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  }
                }}
              >
                {busy ? "Generating PDF..." : "Download PDF"}
              </Button>
              
              <Button
                variant="outlined"
                size="medium"
                startIcon={<WhatsApp />}
                onClick={shareToWhatsApp}
                disabled={busy}
                sx={{
                  minWidth: { xs: "100%", sm: 150 },
                  maxWidth: { xs: "100%", sm: 150 },
                  height: 48,
                  color: "#25D366",
                  borderColor: "#25D366",
                  borderWidth: "2px",
                  fontSize: { xs: "0.875rem", sm: "0.7rem", md: "0.875rem" },
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#20B858",
                    borderWidth: "2px",
                    backgroundColor: "rgba(37, 211, 102, 0.04)",
                  },
                  "&:disabled": {
                    opacity: 0.6,
                  }
                }}
              >
                {busy ? "Processing..." : "Share to WhatsApp"}
              </Button>
              
              <Button
                variant="contained"
                size="medium"
                startIcon={<ArrowBack />}
                onClick={goBack}
                sx={{
                  minWidth: { xs: "100%", sm: 150 },
                  maxWidth: { xs: "100%", sm: 150 },
                  height: 48,
                  fontSize: { xs: "0.875rem", sm: "0.7rem", md: "0.875rem" },
                  fontWeight: 500,
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "2px solid transparent",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a67d8 0%, #6c5b7b 100%)",
                  },
                }}
              >
                Back to History
              </Button>
            </Stack>

            {/* Loading indicator */}
            {busy && (
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 1,
                  fontStyle: "italic" 
                }}
              >
                Processing your request...
              </Typography>
            )}
          </Stack>
        </Card>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ open: false, message: "" })}
          message={snackbar.message}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        />
      </Container>
    </Box>
  );
}
